import { Injectable, NgModule } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterModule,
  Routes,
} from '@angular/router';
import {
  FeatureToggleModule,
  NgxFeatureToggleRouteGuard,
} from 'ngx-feature-toggle';


/* Resolvers */
import { HuntResolver } from './pages/hunt/resolvers/hunt.resolver';
/* Guards */
import { AuthGuard } from './auth/auth-guard.service';
import { FilterComponent } from './modules/research/components/filter/filter.component';
import { huntGuard } from './pages/hunt/guards/hunt.guard';
/* 404 */
import { FourOhFourComponent } from './screens/404/404.component';
/* Customer Support */
import { ContactUsComponent } from './screens/customer-support/contact.component';
import { LoginComponent } from './screens/portal/login/login.component';
import { NewPasswordComponent } from './screens/portal/new-password/new-password.component';
/* Portal */
import { PortalComponent } from './screens/portal/portal.component';
import { ResetPasswordComponent } from './screens/portal/reset-password/reset-password.component';
import { PremiumGateComponent } from './screens/premium-gate/premium-gate.component';
/* Legal */
import { PrivacyComponent } from './screens/privacy-policy/privacy.component';
import { TrialGateComponent } from './screens/trial-gate/trial-gate.component';
import { FooterOnlyLayoutComponent } from './layouts/footer-only-layout/footer-only-layout.component';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { IframeGuard } from './modules/shared/guards/iframe.guard';

import { HuntPackageDetailsComponent } from './pages/hunt/hunt-package-details/hunt-package-details.component';
import { ArchiveHuntGuard } from './modules/hunt/archived-hunt.guard';
import { HuntWorkbenchComponent } from './modules/hunt-redesign/hunt-workbench/hunt-workbench.component';
import { HuntEditReportComponent } from './modules/hunt-redesign/hunt-edit-report/hunt-edit-report.component';
import { HuntFinalReportComponent } from './modules/hunt-redesign/hunt-final-report/hunt-final-report.component';
import { FinalReportOverviewComponent } from './modules/hunt-redesign/hunt-final-report/final-report-overview/final-report-overview.component';
import { FinalReportConclusionComponent } from './modules/hunt-redesign/hunt-final-report/final-report-conclusion/final-report-conclusion.component';
import { FinalReportHuntPackageComponent } from './modules/hunt-redesign/hunt-final-report/final-report-hunt-package/final-report-hunt-package.component';
import { EditReportConclusionComponent } from './modules/hunt-redesign/hunt-edit-report/edit-report-conclusion/edit-report-conclusion.component';
import { EditReportOverviewComponent } from './modules/hunt-redesign/hunt-edit-report/edit-report-overview/edit-report-overview.component';
import { EditReportHuntPackageComponent } from './modules/hunt-redesign/hunt-edit-report/edit-report-hunt-package/edit-report-hunt-package.component';
import { HuntRedirectComponent } from './modules/hunt-redesign/hunt-redirect/hunt-redirect.component';
import { HuntRedesignFeatureGuard } from './modules/hunt-redesign/hunt-redesign.guard';
@Injectable({
  providedIn: 'root',
})
export class RedirectGuard implements CanActivate {
  public canActivate(route: ActivatedRouteSnapshot): boolean {
    window.location.href = route.data['externalUrl'];
    return true;
  }
}

const routes: Routes = [
  // Routes with both header and footer
  {
    path: '',
    component: MainLayoutComponent,
    children: [
      {
        path: 'research',
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/research/research.module').then(m => m.ResearchModule),
      },
      { path: '', redirectTo: 'research', pathMatch: 'full' },
      {
        path: 'settings',
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/settings/settings.module').then(m => m.SettingsModule),
      },
      {
        path: 'hunt',
        canActivate: [AuthGuard],
        canLoad: [NgxFeatureToggleRouteGuard],
        data: {
          featureToggle: ['huntModule'],
          redirectTo: '/splash',
        },
        loadChildren: () => import('./modules/hunt/hunt.module').then(m => m.HuntModule),
      },
      {
        path: 'releases',
        canActivate: [AuthGuard],
        loadChildren: () => import('./changelog/release.module').then(m => m.ReleaseModule),
      },
      {
        path: 'customer-support',
        component: ContactUsComponent,
      },
      {
        path: 'trial-gate',
        component: TrialGateComponent,
      },
      {
        path: 'premium-gate',
        component: PremiumGateComponent,
      },
      {
        path: '404',
        component: FourOhFourComponent,
      },
      // Legacy routes within MainLayout
      {
        path: 'list/hunt/:id',
        redirectTo: 'hunt/:id',
      },
      {
        path: 'list',
        redirectTo: 'hunt',
      },
      {
        path: 'hunt-redesign/:id',
        canActivate: [HuntRedesignFeatureGuard, ArchiveHuntGuard],
        resolve: { hunt: HuntResolver },
        children: [
          {
            path: '',
            component: HuntRedirectComponent,
          },
          {
            path: 'workbench',
            children: [
              { path: '', component: HuntWorkbenchComponent },
              { path: ':packageId', component: HuntPackageDetailsComponent },
            ]
          },
          {
            path: 'edit-report',
            component: HuntEditReportComponent,
            children: [
              {
                path: 'overview',
                component: EditReportOverviewComponent
              },
              {
                path: 'conclusion',
                component: EditReportConclusionComponent
              },
              {
                path: ':packageId',
                component: EditReportHuntPackageComponent
              },
              {
                path: '',
                pathMatch: 'full', 
                redirectTo: 'overview'
              }
            ]
          },
          {
            path: 'final-report',
            component: HuntFinalReportComponent,
            children: [
              {
                path: 'overview',
                component: FinalReportOverviewComponent
              },
              {
                path: 'conclusion',
                component: FinalReportConclusionComponent
              },
              {
                path: ':packageId',
                component: FinalReportHuntPackageComponent
              },
              {
                path: '',
                pathMatch: 'full',
                redirectTo: 'overview'
              }
            ]
          },
        ],
      },
      {
        path: 'details',
        canActivate: [AuthGuard],
        children: [
          {
            path: 'use-case/:hunt_package_uuid',
            redirectTo: '/research/hunt-package/:hunt_package_uuid',
          },
          {
            path: 'threat-profile/:uuid',
            redirectTo: '/research/threat-profile/:uuid',
          },
        ],
      },
    ]
  },
  
  // Routes with footer only (no header)
  {
    path: '',
    component: FooterOnlyLayoutComponent,
    children: [
      {
        path: 'login',
        component: PortalComponent,
        canActivate: [IframeGuard],
        children: [{ path: '', component: LoginComponent }],
      },
      {
        path: 'new-password',
        component: PortalComponent,
        canActivate: [IframeGuard],
        children: [{ path: '', component: NewPasswordComponent }],
      },
      {
        path: 'reset-password',
        component: PortalComponent,
        canActivate: [IframeGuard],
        children: [{ path: '', component: ResetPasswordComponent }],      
      },
      {
        path: 'privacy-policy',
        component: PrivacyComponent,
      },
    ]
  },
  
  // Splash page with filter component (if it needs special handling)
  {
    path: 'splash',
    component: FilterComponent,
    resolve: {
      url: 'externalUrlRedirectResolver',
    },
    data: {
      externalUrl: 'https://www.cyborgsecurity.com/hunt-management-module/',
    },
  },
  
  // External redirects (don't need layout)
  {
    path: 'terms-and-conditions',
    component: RedirectGuard,
    canActivate: [RedirectGuard],
    data: {
      externalUrl: '/assets/docs/terms-and-conditions/hunter-terms-and-conditions.pdf',
    },
  },
  {
    path: 'community-terms',
    component: RedirectGuard,
    canActivate: [RedirectGuard],
    data: {
      externalUrl: '/assets/docs/terms-and-conditions/hunter-community-terms.pdf',
    },
  },
  {
    path: 'eval-agreement',
    component: RedirectGuard,
    canActivate: [RedirectGuard],
    data: {
      externalUrl: '/assets/docs/terms-and-conditions/hunter-evaluation-terms.pdf',
    },
  },
  
  { path: '**', redirectTo: '/404' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: 'reload',
      anchorScrolling: 'enabled',
      scrollPositionRestoration: 'enabled',
    }),
    FeatureToggleModule,
  ],
  exports: [RouterModule],
  providers: [
    {
      provide: 'externalUrlRedirectResolver',
      useValue: (route: ActivatedRouteSnapshot): void => {
        window.location.href = route.data.externalUrl;
      },
    },
  ],
})
export class AppRoutingModule {}